import { formatRelative } from '@solar/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, CheckCheck } from 'lucide-react';
import { Link } from 'react-router-dom';

import { StatusBadge } from '@/components/common/StatusBadge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { queryKeys } from '@/lib/query-client';
import { notificationsService } from '@/services/notifications.service';
import { cn } from '@/lib/utils';

/**
 * Notification bell.
 *
 * The unread count polls on a one-minute interval — enough to feel live without a
 * WebSocket, which would be the right upgrade once approvals start driving real-time
 * work. The list itself is only fetched when the menu opens.
 */
export const NotificationsMenu = () => {
  const queryClient = useQueryClient();

  const unread = useQuery({
    queryKey: queryKeys.notifications.unreadCount,
    queryFn: () => notificationsService.unreadCount(),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
  });

  const recent = useQuery({
    queryKey: queryKeys.notifications.list({ pageSize: 8 }),
    queryFn: ({ signal }) => notificationsService.list({ pageSize: 8 }, signal),
  });

  const markRead = useMutation({
    mutationFn: (ids?: string[]) => notificationsService.markRead(ids),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all });
    },
  });

  const count = unread.data?.total ?? 0;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          className="relative"
          aria-label={count > 0 ? `Notifications, ${count} unread` : 'Notifications'}
        >
          <Bell />
          {count > 0 ? (
            <span className="absolute -right-0.5 -top-0.5 flex min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[0.625rem] font-semibold leading-4 text-destructive-foreground">
              {count > 99 ? '99+' : count}
            </span>
          ) : null}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent className="w-80 p-0">
        <div className="flex items-center justify-between px-3 py-2">
          <p className="text-xs font-semibold">Notifications</p>
          {count > 0 ? (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 gap-1 text-2xs"
              loading={markRead.isPending}
              onClick={() => markRead.mutate(undefined)}
            >
              <CheckCheck className="size-3" />
              Mark all read
            </Button>
          ) : null}
        </div>
        <DropdownMenuSeparator className="mx-0 my-0" />

        <div className="max-h-96 overflow-y-auto">
          {recent.isLoading ? (
            <p className="px-3 py-8 text-center text-xs text-muted-foreground">Loading…</p>
          ) : (recent.data?.items.length ?? 0) === 0 ? (
            <p className="px-3 py-8 text-center text-xs text-muted-foreground">
              You are all caught up.
            </p>
          ) : (
            recent.data?.items.map((notification) => {
              const content = (
                <div
                  className={cn(
                    'space-y-1 border-b border-border/60 px-3 py-2.5 transition-colors last:border-0',
                    !notification.isRead && 'bg-accent/40',
                  )}
                >
                  <div className="flex items-start gap-2">
                    <span className="min-w-0 flex-1 text-xs font-medium leading-snug">
                      {notification.title}
                    </span>
                    <StatusBadge status={notification.type} withDot={false} />
                  </div>
                  {notification.body ? (
                    <p className="line-clamp-2 text-2xs text-muted-foreground">{notification.body}</p>
                  ) : null}
                  <p className="text-2xs text-muted-foreground/80">
                    {formatRelative(notification.createdAt)}
                  </p>
                </div>
              );

              return notification.link ? (
                <Link
                  key={notification.id}
                  to={notification.link}
                  onClick={() => !notification.isRead && markRead.mutate([notification.id])}
                  className="block hover:bg-accent/60"
                >
                  {content}
                </Link>
              ) : (
                <div key={notification.id}>{content}</div>
              );
            })
          )}
        </div>

        <DropdownMenuSeparator className="mx-0 my-0" />
        <Link
          to="/notifications"
          className="block px-3 py-2 text-center text-xs font-medium text-primary hover:bg-accent/60"
        >
          View all notifications
        </Link>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
